/**
 * SLDStandaloneViewer — standalone SLD window content, renders inside portal or Electron child window.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useMonitoringStore } from '@/stores';
import DataPointConfigModal from '../DataPointConfigModal';
import PointDetailPopup from './PointDetailPopup';
import type { AlarmState } from '@/types/monitoring';
import type { SLDPointMapping, SLDDiagram } from '@/stores/monitoringStore';
import {
  EMPTY_DATAPOINTS,
  EMPTY_ADVANCED_RULES,
  EMPTY_MAPPINGS,
  EMPTY_RULE_MAPPINGS,
  EMPTY_TRIGGERED,
  clamp,
  stateClass,
  renderMetricValues,
} from './sldUtils';

export function SLDStandaloneViewer({ diagram }: { diagram: SLDDiagram }) {
  const runtimeStates = useMonitoringStore((s) => s.runtimeStates);
  const projectConfig = useMonitoringStore((s) => s.projectConfig);
  const triggeredAdvancedRules = useMonitoringStore((s) => s.triggeredAdvancedRules);
  const updateSLD = useMonitoringStore((s) => s.updateSLD);

  const datapoints = projectConfig?.datapoints ?? EMPTY_DATAPOINTS;
  const advancedRules = projectConfig?.advanced_rules ?? EMPTY_ADVANCED_RULES;
  const triggered = triggeredAdvancedRules ?? EMPTY_TRIGGERED;

  const dpMappings = diagram.dataPointMappings ?? EMPTY_MAPPINGS;
  const ruleMappings = diagram.advancedRuleMappings ?? EMPTY_RULE_MAPPINGS;

  // Point detail popup (local state for standalone viewer)
  const [detailMapping, setDetailMapping] = useState<SLDPointMapping | null>(null);
  const [detailPos, setDetailPos] = useState<{ x: number; y: number } | null>(null);
  const [detailMetric, setDetailMetric] = useState('');
  const [editingDpId, setEditingDpId] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const viewRef = useRef({ x: 0, y: 0, scale: 1 });
  const panRef = useRef({ active: false, startX: 0, startY: 0 });
  const [zoomPct, setZoomPct] = useState(100);

  const applyTransform = useCallback(() => {
    const { x, y, scale } = viewRef.current;
    const t = `translate(${x}px, ${y}px) scale(${scale})`;
    if (canvasRef.current) canvasRef.current.style.transform = t;
    if (overlayRef.current) overlayRef.current.style.transform = t;
    setZoomPct(Math.round(scale * 100));
  }, []);

  const fitToView = useCallback(() => {
    const img = imageRef.current;
    const container = containerRef.current;
    if (!img || !container) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (!img.width || !img.height) return;
    const scale = Math.min(cw / img.width, ch / img.height) * 0.95;
    viewRef.current = { scale, x: (cw - img.width * scale) / 2, y: (ch - img.height * scale) / 2 };
    applyTransform();
  }, [applyTransform]);

  // Load image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d')?.drawImage(img, 0, 0);
      }
      fitToView();
    };
    img.src = diagram.imageUrl;
  }, [diagram.imageUrl, fitToView]);

  // Pan + zoom events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = viewRef.current;
      const oldScale = v.scale;
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newScale = clamp(oldScale * (1 + delta), 0.05, 10);
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      v.x = cx - ((cx - v.x) / oldScale) * newScale;
      v.y = cy - ((cy - v.y) / oldScale) * newScale;
      v.scale = newScale;
      const t = `translate(${v.x}px, ${v.y}px) scale(${v.scale})`;
      if (canvasRef.current) canvasRef.current.style.transform = t;
      if (overlayRef.current) overlayRef.current.style.transform = t;
      setZoomPct(Math.round(v.scale * 100));
    };
    container.addEventListener('wheel', onWheel, { passive: false });

    const onMouseMove = (e: MouseEvent) => {
      if (!panRef.current.active) return;
      viewRef.current.x = e.clientX - panRef.current.startX;
      viewRef.current.y = e.clientY - panRef.current.startY;
      const v = viewRef.current;
      const t = `translate(${v.x}px, ${v.y}px) scale(${v.scale})`;
      if (canvasRef.current) canvasRef.current.style.transform = t;
      if (overlayRef.current) overlayRef.current.style.transform = t;
    };

    const ownerDoc = container.ownerDocument;
    const ownerWin = ownerDoc.defaultView!;
    ownerWin.addEventListener('mousemove', onMouseMove);

    const onMouseUp = () => {
      if (panRef.current.active) {
        panRef.current.active = false;
        container.classList.remove('dragging');
      }
    };
    ownerWin.addEventListener('mouseup', onMouseUp);

    return () => {
      container.removeEventListener('wheel', onWheel);
      ownerWin.removeEventListener('mousemove', onMouseMove);
      ownerWin.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    panRef.current = { active: true, startX: e.clientX - viewRef.current.x, startY: e.clientY - viewRef.current.y };
    containerRef.current?.classList.add('dragging');
  }, []);

  const iw = imageRef.current?.width ?? 0;
  const ih = imageRef.current?.height ?? 0;

  return (
    <div className="sld-standalone-viewer">
      <div className="sld-standalone-header">
        <span className="sld-title">{diagram.name}</span>
        <span className="sld-zoom-display">{zoomPct}%</span>
      </div>
      <div className="sld-canvas-area" style={{ flex: 1 }}>
        <div
          ref={containerRef}
          className="sld-canvas-container"
          onMouseDown={handleMouseDown}
        >
          <canvas ref={canvasRef} className="sld-canvas" />
          <div ref={overlayRef} className="sld-overlay-layer" style={{ width: iw, height: ih }}>
            {dpMappings.map((mapping) => {
              const runtime = runtimeStates.get(mapping.dataPointId);
              const config = datapoints.find((dp) => dp.id === mapping.dataPointId);
              const state: AlarmState = runtime?.state ?? 'no_data';
              return (
                <div
                  key={`dp-${mapping.dataPointId}`}
                  className="sld-point sld-point--clickable"
                  style={{ left: mapping.x, top: mapping.y }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailMapping(mapping);
                    setDetailPos({ x: e.clientX, y: e.clientY });
                    setDetailMetric(mapping.displayMetric ?? '');
                  }}
                >
                  <span className={`sld-point-dot ${stateClass(state)}`} />
                  <span className="sld-point-label">{config?.display_name ?? 'Unknown'}</span>
                  {renderMetricValues(config, runtime, mapping.displayMetric)}
                </div>
              );
            })}
            {ruleMappings.map((mapping) => {
              const trig = triggered.find((r) => r.rule_id === mapping.advancedRuleId);
              const rule = advancedRules.find((r) => r.id === mapping.advancedRuleId);
              const ruleState = trig ? trig.severity : 'ok';
              return (
                <div key={`rule-${mapping.advancedRuleId}`} className="sld-point sld-point--rule" style={{ left: mapping.x, top: mapping.y }}>
                  <span className={`sld-point-dot sld-point-dot--diamond ${stateClass(ruleState)}`} />
                  <span className="sld-point-label">{rule?.name ?? 'Rule'}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Point Detail Popup ── */}
      {detailMapping && detailPos && (
        <>
          <div className="sld-detail-backdrop" onClick={() => setDetailMapping(null)} />
          <PointDetailPopup
            mapping={detailMapping}
            position={detailPos}
            datapoints={datapoints}
            runtimeStates={runtimeStates}
            advancedRules={advancedRules}
            triggeredAdvancedRules={triggered}
            displayMetric={detailMetric}
            onDisplayMetricChange={setDetailMetric}
            onSave={() => {
              if (!detailMapping) return;
              const newMappings = dpMappings.map((m) =>
                m.dataPointId === detailMapping.dataPointId ? { ...m, displayMetric: detailMetric || undefined } : m,
              );
              updateSLD(diagram.id, { dataPointMappings: newMappings });
              setDetailMapping(null);
            }}
            onRemove={(dpId) => {
              updateSLD(diagram.id, {
                dataPointMappings: dpMappings.filter((m) => m.dataPointId !== dpId),
              });
              setDetailMapping(null);
            }}
            onEdit={(dpId) => {
              setEditingDpId(dpId);
              setDetailMapping(null);
            }}
            onLocateOnMap={() => {}}
            onClose={() => setDetailMapping(null)}
          />
        </>
      )}

      {/* ── Inline Data Point Editor (stays on top of standalone SLD) ── */}
      {editingDpId && (
        <DataPointConfigModal
          isOpen={true}
          onClose={() => setEditingDpId(null)}
          datapointId={editingDpId}
        />
      )}
    </div>
  );
}
